import {
  APILoadingStatus,
  APIProvider,
  Map,
  Polygon,
  Polyline,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps';
import { Spin } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';

import { boundsIntersect } from '@/lib/maps/boundaries';
import { createCameraAdapter } from '@/lib/maps/camera';
import type {
  InitialView,
  MapBounds,
  MapCamera,
  MapCameraAdapter,
  MapPoint,
  MapPolygonStyle,
  MapProjection,
} from '@/lib/maps/types';
import { MAX_ZOOM, MIN_ZOOM } from '@/lib/maps/zoom';

import styles from './map-canvas.module.scss';

export type MapCanvasProps = {
  /** `env.VITE_GOOGLE_MAPS_API_KEY`, passed by the page. Optional in `env.ts`, so absence must
   *  degrade rather than crash — and a test can render the canvas path by passing a stub. */
  apiKey?: string;
  /**
   * The one-shot initial view, or `null` while the Reference location is still resolving. Null shows
   * the loading state and does **not** mount `<Map>`: the camera is uncontrolled, so a map mounted
   * before the view arrived could only be corrected by a controlled camera, which ADR-0016 rules
   * out. Keeping the SDK load and the chain fetch concurrent is the reason this is a prop rather
   * than a branch in the page.
   */
  initialView: InitialView | null;
  /** Rendered in place of the canvas when there is no key, or Google rejects it. */
  unavailable: ReactNode;
  /** Receives the camera seam once the map is live (ADR-0017). */
  onAdapterReady?: (adapter: MapCameraAdapter) => void;
  /** Injected in place of the live map's adapter — the test seam for camera commands. */
  adapter?: MapCameraAdapter;
  /** Every camera frame — the centre and zoom as the map reports them. The zoom controls disable
   *  themselves off this, and ticket 03's viewport memory saves it. */
  onCameraChanged?: (camera: MapCamera) => void;
  /** Overlays drawn on the canvas: `<MapPolygon>` Boundaries (ticket 05), Device markers (ticket 06). */
  children?: ReactNode;
  /**
   * Every click on the canvas, in the wire spelling. **One route for every click** — a marker's own
   * `<button>` still swallows its press first, and everything else arrives here for `resolveMapClick`
   * to arbitrate (113586 §6a, §3h, §9a). A right-click is a separate SDK event and never arrives, so
   * "a right-click opens no panel and focuses nothing" (§8e) needs no code.
   */
  onMapClick?: (point: MapPoint) => void;
  /**
   * The projection in **both** directions — lat/lon → map-area pixel and back — republished on
   * **every camera frame**, which is what makes an anchored panel and a name label travel with the
   * map (113586 §4d) with nothing subscribing to the camera, and what lets a drag resolve a pointer
   * position against the camera it is actually on.
   *
   * **Hand down a stable callback** (`useCallback`): it is an effect dependency below, so a fresh
   * arrow every render would tear the `OverlayView` down and rebuild it on every frame, forever.
   */
  onProjectionChanged?: (projection: MapProjection) => void;
  /**
   * The cursor over the map while no overlay claims it — `'crosshair'` for a Field Mapping session
   * (113600: "the cursor indicates the map is in marking mode"). Left unset on the read-only Map, so
   * Google's own grab/grabbing pair still says "this pans".
   */
  cursor?: string;
  /**
   * `false` suppresses Google's own gesture handling — pan, wheel-zoom, pinch and double-click — for
   * as long as it is set. Field Mapping passes `false` while a **Point** is being dragged: the
   * marker lives in the map's own `floatPane`, so without this the map would pan underneath the
   * gesture. The whole gesture set goes rather than panning alone, because `gestureHandling` is a
   * single mode and there is no half-setting.
   */
  panningEnabled?: boolean;
  /**
   * A double-click on the canvas. Google fires `click` **before** `dblclick` (sometimes twice), so a
   * screen that marks on click needs to hear this to un-mark what the zoom gesture would otherwise
   * have left behind (113600: double-click "zooms and marks no Point").
   */
  onMapDoubleClick?: () => void;
};

/**
 * One of the **two** modules in the app permitted to import `@vis.gl/react-google-maps` — this one
 * and `map-overlay.tsx` beside it — enforced file by file by `no-restricted-imports` in
 * `eslint.config.js`. Feature code reaches the map through this component and the
 * {@link MapCameraAdapter} seam, never through the SDK (ADR-0017).
 *
 * Satellite imagery, no default Google chrome (`<MapControlStack>` draws the project's own
 * controls), and it takes the resolved view as `defaultBounds` **or** `defaultCenter`/`defaultZoom`
 * — either way an uncontrolled camera React never re-applies, so a refetch cannot move the Grower's
 * viewport (ADR-0016: fit-bounds runs once per load and never on a refetch).
 */
export function MapCanvas({ apiKey, unavailable, ...rest }: MapCanvasProps) {
  if (!apiKey) return <>{unavailable}</>;
  return (
    <APIProvider apiKey={apiKey}>
      <Surface unavailable={unavailable} {...rest} />
    </APIProvider>
  );
}

function Surface({
  initialView,
  unavailable,
  onAdapterReady,
  adapter,
  onCameraChanged,
  children,
  onMapClick,
  onProjectionChanged,
  cursor,
  panningEnabled,
  onMapDoubleClick,
}: Omit<MapCanvasProps, 'apiKey'>) {
  const status = useApiLoadingStatus();

  // Google rejected the key (invalid / unauthorised / no billing). Degrade to the caller's notice
  // rather than let Google paint its own error overlay across the map area.
  if (status === APILoadingStatus.AUTH_FAILURE || status === APILoadingStatus.FAILED) {
    return <>{unavailable}</>;
  }

  // 113583 §1d / 113584 §14a: a loading state rather than a blank viewport. Admin has no branch here
  // because its page owns an `isPending` from a snapshot query; this app's Map has no query yet, so
  // the SDK's own load status is the honest source. Later tickets layer their query pending states
  // over the same box.
  // Either half can still be outstanding, and they run concurrently: the Maps SDK script, and the
  // Reference location chain that decides where to open (113584 §1). Same box for both.
  if (status !== APILoadingStatus.LOADED || !initialView) {
    return (
      <div className={styles.centered}>
        <Spin aria-label="Loading map" />
      </div>
    );
  }

  // One of the two, never both: vis.gl warns when it gets neither and prefers bounds over centre.
  const camera =
    initialView.kind === 'fit'
      ? { defaultBounds: { ...initialView.bounds, padding: initialView.padding } }
      : {
          defaultCenter: { lat: initialView.center.lat, lng: initialView.center.lon },
          defaultZoom: initialView.zoom,
        };

  return (
    <Map
      mapTypeId="satellite"
      {...camera}
      // The supported range, bounding every gesture (113584 §5e) with the same two constants that
      // disable the controls at a limit, so the two can never drift apart. It also bounds the `fit`
      // branch above: a Boundary tight enough to want more than `MAX_ZOOM` frames at `MAX_ZOOM`.
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      // Drag pans, wheel/trackpad/pinch zooms, double-click zooms in one step (113584 §5a–§5c) —
      // unless a screen has taken the gestures away for the length of its own (see the prop).
      gestureHandling={panningEnabled === false ? 'none' : 'greedy'}
      // Arrow keys pan and +/- zoom once the map has focus (113584 §5d). `disableDefaultUI` below
      // switches off Google's own controls, NOT this — stated explicitly so the two are never
      // confused for each other.
      keyboardShortcuts
      disableDefaultUI
      onCameraChanged={(event) =>
        onCameraChanged?.({
          center: { lat: event.detail.center.lat, lon: event.detail.center.lng },
          zoom: event.detail.zoom,
        })
      }
      onClick={(event) => {
        const latLng = event.detail.latLng;
        // A click on map chrome can arrive with no position; there is nothing to arbitrate then.
        if (latLng) onMapClick?.({ lat: latLng.lat, lon: latLng.lng });
      }}
      draggableCursor={cursor}
      onDblclick={() => onMapDoubleClick?.()}
      className={styles.map}
    >
      <AdapterBridge onAdapterReady={onAdapterReady} adapter={adapter} />
      <ProjectionBridge onProjectionChanged={onProjectionChanged} />
      {children}
    </Map>
  );
}

/** Hands the camera seam up once the map instance exists. An injected `adapter` wins, which is how
 *  a test drives camera commands with no live map. */
function AdapterBridge({
  onAdapterReady,
  adapter,
}: Pick<MapCanvasProps, 'onAdapterReady' | 'adapter'>) {
  const map = useMap();
  useEffect(() => {
    if (!onAdapterReady) return;
    if (adapter) onAdapterReady(adapter);
    else if (map) onAdapterReady(createCameraAdapter(map));
  }, [map, adapter, onAdapterReady]);
  return null;
}

/**
 * Publishes the lat/lon ↔ map-area-pixel projection, in both directions.
 *
 * An invisible `OverlayView` is the only way to reach `fromLatLngToContainerPixel`:
 * `map.getProjection()` answers in **world** coordinates, not screen ones. The SDK calls `draw()` on
 * every camera change, so republishing a fresh closure there is what makes an anchored panel and a
 * name label follow the map without either of them subscribing to anything.
 *
 * `onProjectionChanged` is a dep, so the consumer MUST hand down a stable callback — see the prop.
 *
 * ponytail: one `setState` per camera frame while a panel is open — the same cost `<MapPolygon>`'s
 * viewport read already pays. Batch through a ref if a Grower's Field count ever makes it visible.
 */
function ProjectionBridge({ onProjectionChanged }: Pick<MapCanvasProps, 'onProjectionChanged'>) {
  const map = useMap();

  useEffect(() => {
    if (!map || !onProjectionChanged || typeof google === 'undefined') return undefined;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {};
    overlay.draw = () => {
      const projection = overlay.getProjection();
      onProjectionChanged({
        toPixel: (point) => {
          const pixel = projection?.fromLatLngToContainerPixel(
            new google.maps.LatLng(point.lat, point.lon),
          );
          return pixel ? { x: pixel.x, y: pixel.y } : null;
        },
        // `fromLatLngToContainerPixel` has no inverse outside an OverlayView either:
        // `map.getProjection()` answers in WORLD coordinates, so only the overlay can turn a screen
        // position back into a coordinate. Same closure as `toPixel` above, so the two can never be
        // published from different camera frames.
        toPoint: ({ x, y }) => {
          const latLng = projection?.fromContainerPixelToLatLng(new google.maps.Point(x, y));
          return latLng ? { lat: latLng.lat(), lon: latLng.lng() } : null;
        },
      });
    };
    overlay.onRemove = () => {};
    overlay.setMap(map);

    return () => overlay.setMap(null);
  }, [map, onProjectionChanged]);

  return null;
}

/** The map's current geographic window, republished on every camera change. `null` before Google has
 *  measured one — a real state on first paint, not an error. */
function useMapViewport(map: ReturnType<typeof useMap>): MapBounds | null {
  const [viewport, setViewport] = useState<MapBounds | null>(null);

  useEffect(() => {
    if (!map) return undefined;
    const read = () => {
      const bounds = map.getBounds();
      if (bounds) setViewport(bounds.toJSON());
    };
    read();
    const listener = map.addListener('bounds_changed', read);
    return () => listener.remove();
  }, [map]);

  return viewport;
}

/** A two-unit segment along the path. Its LENGTH comes from the symbol's `scale`, its THICKNESS from
 *  `strokeWeight` — see `MapPolygonStyle.dashLength`. */
const DASH_SYMBOL_PATH = 'M 0,-1 0,1';

export type MapPolygonProps = {
  /** Every ring of one Field's Boundary, exterior ring first. */
  rings: readonly (readonly MapPoint[])[];
  /** The overlay's own geographic box, so it can cull itself when the viewport moves off it. */
  bounds: MapBounds;
  style: MapPolygonStyle;
  /**
   * Whether this Boundary takes the pointer. `false` draws it as **context** — no click target and
   * no hover deepening or pointer cursor — which is what Field Mapping mode's "displayed, and not
   * clickable" means (113600, Field Mapping Screen Elements). Defaults to `true`, so `<MapPage>`
   * keeps the hover-and-click behaviour ticket 07 built.
   */
  interactive?: boolean;
  /**
   * Forwards a click on this Boundary's fill into `resolveMapClick`'s arbitration — the SAME route a
   * canvas click already travels (QA round 2 D1). A **clickable** Google `Polygon` consumes the press
   * before it ever reaches `<Map onClick>`, so without this a click inside a Boundary reached nothing
   * at all. Only wired when `interactive`; Field Mapping's context Boundaries pass none.
   */
  onClick?: (point: MapPoint) => void;
};

/**
 * One Boundary overlay: a translucent fill with a **dashed** outline (Figma `787:679336`).
 *
 * Two SDK primitives per ring, and not by choice — a `google.maps.Polygon` stroke cannot be dashed,
 * so the polygon carries `strokeWeight: 0` and each ring is outlined by a closed `<Polyline>` whose
 * own stroke is invisible and whose `icons` repeat a one-segment dash. Where two adjacent Fields
 * share an edge, both outline it and their dash phases differ, so the shared edge reads as a denser
 * line of the **same weight** — no gap, no thickening, and each Field still individually
 * distinguishable (113586 §1b, §1c).
 *
 * The geometry handed to the SDK is the Field's own coordinates, with **no projection and no
 * screen-space arithmetic anywhere in the path** — which is the whole of "staying geographically
 * anchored" (§1e). The other half of §1e is the cull below: the map reports its window on every
 * camera change, so an overlay leaves the SDK as it leaves view and returns as it comes back.
 *
 * Hovering deepens this overlay's fill and sets a pointer cursor, and does nothing else: no focus,
 * no label, no change to any other Field. Clicking is ticket 07's.
 *
 * A ring of fewer than three points draws as an open dashed chain with no fill — the shape a
 * Grower has started but not closed.
 */
export function MapPolygon({ rings, bounds, style, interactive = true, onClick }: MapPolygonProps) {
  const map = useMap();
  const viewport = useMapViewport(map);
  const [hovered, setHovered] = useState(false);

  // The asymmetry `onClick` has to absorb: the canvas's own `<Map onClick>` hands back a PLAIN
  // object (`event.detail.latLng`), while a Polygon's own click event carries a `LatLng` INSTANCE
  // (`.lat()`/`.lng()` methods) — both are handled here so `<MapPage>` can pass the very same
  // callback to both routes.
  const handleClick =
    interactive && onClick
      ? (event: google.maps.MapMouseEvent) => {
          const latLng = event.latLng;
          if (!latLng) return;
          const lat =
            typeof latLng.lat === 'function'
              ? latLng.lat()
              : (latLng as unknown as { lat: number }).lat;
          const lng =
            typeof latLng.lng === 'function'
              ? latLng.lng()
              : (latLng as unknown as { lng: number }).lng;
          onClick({ lat, lon: lng });
        }
      : undefined;

  // The pointer cursor is an effect rather than a line in the two handlers so that it is undone by
  // React, not by a mouse-out that may never arrive: an overlay hovered at the moment a pan culls it
  // unmounts without one, and would otherwise leave the whole map stuck on `pointer`.
  useEffect(() => {
    if (!map || !hovered) return undefined;
    map.setOptions({ draggableCursor: 'pointer' });
    return () => map.setOptions({ draggableCursor: null });
  }, [map, hovered]);

  // Unknown viewport ⇒ draw. Never hide a Field because Google has not measured yet.
  if (viewport && !boundsIntersect(bounds, viewport)) return null;

  // The ONE place `lon` becomes Google's `lng`. Everything upstream of here speaks `MapPoint`.
  const paths = rings.map((ring) => ring.map(({ lat, lon }) => ({ lat, lng: lon })));
  // A ring below three points is a chain with no enclosed area — Field Mapping mode's shape before
  // its third **Point**. Every other caller is a saved Boundary, which `isDrawableRing` already
  // guarantees is three or more, so this only ever changes behaviour for that one new caller.
  const hasArea = rings.some((ring) => ring.length >= 3);

  return (
    <>
      {hasArea ? (
        <Polygon
          paths={paths}
          fillColor={style.fillColor}
          fillOpacity={hovered ? style.fillOpacityHover : style.fillOpacity}
          strokeWeight={0}
          clickable={interactive}
          onClick={handleClick}
          onMouseOver={interactive ? () => setHovered(true) : undefined}
          onMouseOut={interactive ? () => setHovered(false) : undefined}
        />
      ) : null}
      {paths.map((path, index) => (
        <Polyline
          // A Boundary's rings carry no identity of their own; their order within the Field is their
          // only stable key.
          key={index}
          // Close back to the first point only once there is an area to enclose; an open chain
          // never repeats its own first point.
          path={path.length >= 3 && path[0] ? [...path, path[0]] : path}
          clickable={false}
          strokeOpacity={0}
          icons={[
            {
              icon: {
                path: DASH_SYMBOL_PATH,
                strokeColor: style.dashColor,
                strokeOpacity: style.dashOpacity,
                strokeWeight: style.dashWeight,
                // The symbol path is two units long, so this is what turns a measured dash LENGTH
                // into Google's scale factor. Never restate the number.
                scale: style.dashLength / 2,
              },
              offset: '0',
              repeat: style.dashRepeat,
            },
          ]}
        />
      ))}
    </>
  );
}
